import React from 'react';
import { blankHoaSnapshot, normalizeHoaSnapshot } from '../HoaDirectory/HoaDirectoryHelper';
import HoaBlock from './HoaBlock';
import HoaBlockSkeleton from './HoaBlockSkeleton';
import RequestHoaInfoTrigger from '../services/pipeline/estoppel/RequestHoaInfoTrigger';
import { ClockPlus } from 'lucide-react';

const HOAS_COUNT = 3;

const HoaForm = ({
  value = [],
  onChange,
  errors = {},
  showCountSelector = true,
  showProcessorFields = false,
  showAddLater = false,
  /** Client-entered HOA reference from order placement (processor view). */
  requestHoaInfo = [],
  /** When set, only that HOA slot is shown (count selector hidden). Full array still in `value`. */
  editIndexOnly = null,
  /** Render visible HOA blocks in a 2-column grid (new order form). */
  twoColumnLayout = false,
  /** Per-slot directory file metadata from parent prefetch (Edit HOAs). */
  prefetchedDirectoryFilesByIndex = null,
  /** When true, show skeleton until prefetchedDirectoryFilesByIndex is ready. */
  directoryPrefetchLoading = false,
  /** Notify parent when a nested directory modal opens/closes (hide parent modal). */
  onNestedDirectoryModalChange,
  onDirectorySlotsSaved,
  /** Report Builder only — permit editing the directory preferred contact from an HOA slot. */
  showDirectoryPreferredContactToggle = false,
  onDirectoryPreferredContactChange,
}) => {
  const hoas = (value || []).map(normalizeHoaSnapshot);
  const addLaterSelected = showAddLater && hoas.length === 0;
  const count = addLaterSelected ? 0 : Math.min(HOAS_COUNT, Math.max(1, hoas.length));
  const singleEdit =
    editIndexOnly != null &&
    Number.isInteger(editIndexOnly) &&
    editIndexOnly >= 0 &&
    editIndexOnly < hoas.length;

  const setCount = n => {
    let next = [...hoas];
    if (n > next.length) {
      while (next.length < n) next.push(blankHoaSnapshot());
    } else {
      next = next.slice(0, n);
    }
    onChange(next);
  };

  const updateHoa = (index, nextHoa) => {
    const next = hoas.length === 0 ? [nextHoa] : [...hoas];
    next[index] = nextHoa;
    onChange(next);
  };

  const visibleIndexes = singleEdit
    ? [editIndexOnly]
    : addLaterSelected
    ? []
    : Array.from({ length: count }, (_, i) => i);

  const hoaAtIndex = index => hoas[index] || blankHoaSnapshot();

  const pillClass = active =>
    `midFont px-3 py-1 cursor-pointer border d-flex align-items-center justify-content-center ${
      active ? 'font-weight-semibold border-info bg-info-light text-info' : 'bg-white text-muted'
    }`;

  const hoaBlockColumnClass = twoColumnLayout ? 'col-md-6 mb-3' : undefined;

  return (
    <div>
      {showCountSelector && !singleEdit && (
        <div className="mb-3">
          <div className="text-uppercase text-muted font-weight-bold xsmallFont mb-2 d-flex align-items-center">
            How many HOAs?
            <RequestHoaInfoTrigger requestHoaInfo={requestHoaInfo} iconSize={14} />
          </div>
          <div className="d-flex" style={{ gap: 8 }} role="group" aria-label="HOA count">
            {Array.from({ length: HOAS_COUNT }, (_, i) => i + 1).map(n => {
              const active = !addLaterSelected && count === n;
              return (
                <button
                  key={n}
                  type="button"
                  className={pillClass(active)}
                  style={{ borderRadius: 10 }}
                  onClick={() => setCount(n)}
                >
                  {n}
                </button>
              );
            })}
            {showAddLater && (
              <button
                type="button"
                className={pillClass(addLaterSelected)}
                style={{ borderRadius: 10 }}
                onClick={() => onChange([])}
              >
                <ClockPlus size={16} className='flex-shrink-0 mr-1'/>
                Add later
              </button>
            )}
          </div>
        </div>
      )}

      {addLaterSelected && (
        <div
          className="text-muted midFont text-center px-3 py-4 bg-muted"
          style={{ border: '1px dashed #adb5bd', borderRadius: 8 }}
        >
          HOA information will be added later.
        </div>
      )}

      <div className={twoColumnLayout ? 'row' : undefined}>
        {directoryPrefetchLoading
          ? visibleIndexes.map(index => (
              <div key={`hoa-skeleton-${index}`} className={hoaBlockColumnClass}>
                <HoaBlockSkeleton />
              </div>
            ))
          : visibleIndexes.map(index => (
              <div
                key={hoas[index]?._id || index}
                className={hoaBlockColumnClass}
              >
                <HoaBlock
                  index={index}
                  hoa={hoaAtIndex(index)}
                  defaultOpen
                  showProcessorFields={showProcessorFields}
                  directoryPickerOnly
                  onChange={next => updateHoa(index, next)}
                  onNestedDirectoryModalChange={onNestedDirectoryModalChange}
                  onDirectoryEntitySaved={nextHoa => {
                    const next = hoas.length === 0 ? [nextHoa] : [...hoas];
                    next[index] = nextHoa;
                    onDirectorySlotsSaved?.(next);
                  }}
                  showDirectoryPreferredContactToggle={showDirectoryPreferredContactToggle}
                  onDirectoryPreferredContactChange={onDirectoryPreferredContactChange}
                  fieldInvalid={{
                    name: Boolean(errors[`name-${index}`] || errors[`HOA${index}Name`]),
                    company: Boolean(
                      errors[`managementCompany-${index}`] || errors[`HOA${index}ManagementCompany`]
                    ),
                    email: Boolean(errors[`email-${index}`] || errors[`HOA${index}Email`]),
                    companyEmail: Boolean(errors[`companyEmail-${index}`]),
                  }}
                  prefetchedDirectoryFiles={prefetchedDirectoryFilesByIndex?.[index] || null}
                />
              </div>
            ))}
      </div>
    </div>
  );
};

export default HoaForm;
export { blankHoaSnapshot, normalizeHoaSnapshot };
